import { useState, useEffect, useRef } from "react";
import { useInView } from "framer-motion";
import { Reveal } from "./Reveal";
import { GraduationCap, Award, ExternalLink, TrendingUp } from "lucide-react";

// Contatore impianti: 1.153 al 20/08/2026, poi +40, +30, +45, +55 a rotazione ogni 3 giorni
const IMPLANT_ANCHOR = Date.UTC(2026, 7, 20);
const IMPLANT_BASE = 1153;
const IMPLANT_STEPS = [40, 30, 45, 55];

const implantCount = () => {
  const periods = Math.max(0, Math.floor((Date.now() - IMPLANT_ANCHOR) / (3 * 864e5)));
  let total = IMPLANT_BASE;
  for (let k = 0; k < periods; k++) total += IMPLANT_STEPS[k % IMPLANT_STEPS.length];
  return total;
};

const fmtNum = (n) => n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");

const ImplantCounter = () => {
  const target = implantCount();
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { margin: "-60px" });

  useEffect(() => {
    if (!inView) { setVal(0); return; }
    let raf;
    const start = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - start) / 2000);
      setVal(Math.round(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, target]);

  return (
    <div
      ref={ref}
      className="bg-[#385244] rounded-[32px] px-8 py-14 md:px-16 md:py-20 flex flex-col md:flex-row items-center justify-center gap-6 md:gap-10 text-center md:text-left"
      data-testid="implant-counter-banner"
    >
      <TrendingUp size={64} strokeWidth={1.5} className="text-[#C98E6E] flex-shrink-0" />
      <p className="font-serif-display font-light text-7xl md:text-8xl lg:text-9xl text-white leading-none" data-testid="implant-counter-value">
        {fmtNum(val)}
      </p>
      <p className="text-lg md:text-2xl text-white/85 max-w-sm leading-snug">
        impianti inseriti dai nostri medici dal 1° gennaio 2026
      </p>
    </div>
  );
};

const doctors = [
  {
    photo: "/dr-gattuso.webp",
    srcset: "/dr-gattuso-480.webp 480w, /dr-gattuso-768.webp 768w, /dr-gattuso.webp 1122w",
    sizes: "(max-width: 768px) 100vw, 600px",
    photoClass: "object-[center_22%]",
    name: "Dr. Massimo Filippo Gattuso",
    role: "Direttore Scientifico del Gruppo Stomatologico Italiano · Medico Chirurgo oro-maxillo-facciale · Docente Master presso l'Università La Sapienza di Roma",
    icon: GraduationCap,
    bio: "Chirurgo oro-maxillo-facciale, si occupa di implantologia da oltre 25 anni, con oltre 2.000 impianti posizionati ogni anno. Docente Master all'Università La Sapienza di Roma e relatore in numerosi convegni, ha pubblicato il libro «Implantologia a carico immediato — Facciamo chiarezza!».",
    book: {
      cover: "/libro-gattuso.webp",
      title: "Implantologia a carico immediato — Facciamo chiarezza!",
      link: "https://www.amazon.it/dp/B0GVHDMQHL",
    },
    specialties: ["Implantologia All on Four", "All on Six", "Impianti pterigoidei", "Griglia iuxtaossea", "Impianti zigomatici"],
  },
  {
    photo: "/dr-giorgi.jpg",
    name: "Dr. Matteo Giorgi",
    role: "Odontoiatra · Opinion Leader per l'implantologia zigomatica · Docente e tutor in corsi di chirurgia in Italia e all'estero",
    icon: Award,
    bio: "Solida esperienza in chirurgia orale, implantologia avanzata e chirurgia bucco-maxillo-facciale, con focus sulla riabilitazione implantare dei pazienti con gravi atrofie mascellari. Riconosciuto a livello internazionale, collabora con aziende leader del settore per la progettazione di impianti innovativi e dispositivi custom-made.",
    education: [
      "Laurea in Odontoiatria e Protesi Dentaria, Università di Pisa (2007)",
      "Master Universitario in Implantologia Zigomatica, Facoltà São Leopoldo Mandic, San Paolo, Brasile (2015)",
      "Specializzazione in Implantologia Zigomatica, Università di Lleida, Maringá, Brasile (2017)",
      "Specializzazione in Chirurgia Bucco-Maxillo-Facciale, Faculdade Facop, Bauru, Brasile (2022)",
    ],
    specialties: ["Implantologia zigomatica", "Implantologia iuxtaossea", "Grandi atrofie mascellari", "Chirurgia bucco-maxillo-facciale"],
  },
];

export const Doctors = () => (
  <section id="medici" className="py-24 md:py-32 px-6 md:px-12" data-testid="doctors-section">
    <div className="max-w-7xl mx-auto">
      <Reveal>
        <ImplantCounter />
      </Reveal>
      <Reveal>
        <p className="mt-16 text-sm uppercase tracking-[0.2em] font-bold text-[#7D9285] mb-4">L'équipe chirurgica</p>
        <h2 className="font-serif-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#15201A] max-w-3xl">
          I nostri <em className="italic text-[#385244]">medici</em> specialisti in implantologia dentale
        </h2>
      </Reveal>
      <div className="mt-14 grid md:grid-cols-2 gap-8 items-start">
        {doctors.map((d, i) => (
          <Reveal key={d.name} delay={i * 0.15}>
            <article className="h-full bg-white border border-border rounded-[32px] overflow-hidden" data-testid={`doctor-card-${i}`}>
              <img
                src={d.photo}
                srcSet={d.srcset}
                sizes={d.sizes}
                alt={d.name}
                loading="lazy"
                decoding="async"
                className={`w-full h-96 object-cover ${d.photoClass || "object-top"}`}
              />
              <div className="p-8 md:p-10">
                <h3 className="font-serif-display text-2xl md:text-3xl text-[#15201A]">{d.name}</h3>
                <p className="mt-4 flex items-start gap-3 text-base leading-relaxed text-[#5C6B61]">
                  <d.icon size={22} className="text-[#C98E6E] flex-shrink-0 mt-0.5" strokeWidth={1.6} />
                  {d.role}
                </p>
                {d.bio && <p className="mt-4 text-sm leading-relaxed text-[#5C6B61]">{d.bio}</p>}
                {d.book && (
                  <div className="mt-6 flex items-center gap-4 bg-[#EAF4F8] border border-border rounded-2xl p-4" data-testid="doctor-book">
                    <a href={d.book.link} target="_blank" rel="noopener noreferrer" className="flex-shrink-0">
                      <img
                        src={d.book.cover}
                        alt={`Copertina del libro ${d.book.title}`}
                        loading="lazy"
                        decoding="async"
                        className="h-28 w-auto rounded-md shadow-md hover:scale-[1.03] transition-transform duration-300"
                      />
                    </a>
                    <div>
                      <p className="text-xs uppercase tracking-[0.15em] font-bold text-[#7D9285]">Autore del libro</p>
                      <p className="mt-1 font-serif-display text-lg leading-snug text-[#15201A]">«{d.book.title}»</p>
                      <a
                        href={d.book.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-2 inline-flex items-center gap-1.5 text-sm font-bold text-[#C98E6E] hover:text-[#B37A5B] transition-colors duration-200"
                        data-testid="doctor-book-amazon-link"
                      >
                        Acquista su Amazon <ExternalLink size={14} />
                      </a>
                    </div>
                  </div>
                )}
                {d.education && (
                  <div className="mt-5">
                    <p className="text-xs uppercase tracking-[0.15em] font-bold text-[#7D9285]">Formazione accademica</p>
                    <ul className="mt-2 space-y-1.5">
                      {d.education.map((e) => (
                        <li key={e} className="text-sm leading-relaxed text-[#5C6B61] pl-4 relative before:content-['·'] before:absolute before:left-0 before:text-[#C98E6E] before:font-bold">
                          {e}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {d.book && (
                  <div className="mt-4 bg-[#385244] rounded-2xl p-5" data-testid="doctor-book-promo">
                    <p className="text-xs uppercase tracking-[0.15em] font-extrabold text-[#C98E6E]">Solo per te!</p>
                    <p className="mt-2 text-sm leading-relaxed text-white/90">
                      Se compili il form riceverai una <strong className="text-white">copia digitale del libro</strong>;
                      se invece farai una visita ti sarà <strong className="text-white">omaggiata la copia cartacea</strong>.
                    </p>
                    <a
                      href="#contatti"
                      className="mt-4 inline-flex items-center rounded-full bg-[#C98E6E] hover:bg-[#B37A5B] text-white text-sm font-bold px-6 py-3 transition-colors duration-300 active:scale-95"
                      data-testid="doctor-book-promo-btn"
                    >
                      Compila il form e ricevi il libro
                    </a>
                  </div>
                )}
                <div className="mt-6 flex flex-wrap gap-2">
                  {d.specialties.map((s) => (
                    <span key={s} className="bg-[#DDEBF2] text-[#385244] text-sm font-bold rounded-full px-4 py-1.5">
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            </article>
          </Reveal>
        ))}
      </div>
      <Reveal>
        <div className="mt-16 max-w-3xl mx-auto text-center">
          <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#7D9285] mb-4">Il Dr. Gattuso si presenta</p>
          <h3 className="font-serif-display font-light text-3xl md:text-4xl text-[#15201A]">
            Conosci il nostro <em className="italic text-[#385244]">Direttore Scientifico</em>
          </h3>
          <video
            controls
            preload="metadata"
            playsInline
            poster="/video/gattuso-poster.jpg"
            className="mt-8 w-full rounded-[28px] border border-border bg-black"
            data-testid="gattuso-presentation-video"
          >
            <source src="/video/gattuso-presentazione.mp4" type="video/mp4" />
            Il tuo browser non supporta la riproduzione video.
          </video>
        </div>
      </Reveal>
    </div>
  </section>
);
